import { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { ContentCopy } from '@mui/icons-material';
import auth from '../../../services/AuthService';
import { toast } from 'react-toastify';
import { useRouteLoaderData } from 'react-router-dom';
import ToastMessage from '@/components/toasts/ToastMessage';

export default function TotpForm() {
  const { me } = useRouteLoaderData('auth');
  const [qrCode, setQrCode] = useState(me?.qrCode || '');
  const [authenticators, setAuthenticators] = useState(
    me?.authenticators?.filter(
      (authenticator) => authenticator.type === 'totp',
    ) || [],
  );
  const [secret, setSecret] = useState('');
  const [qrCodeDialogOpen, setQrCodeDialogOpen] = useState(false);

  const setup2FA = async () => {
    if (!qrCode) {
      try {
        const response = await auth.post('/otp/generate');
        const { qrCodeLink, totpSecret } = response.data;

        setQrCode(qrCodeLink);
        setSecret(totpSecret);
        setQrCodeDialogOpen(true);
      } catch (e) {
        toast.error(
          <ToastMessage title="Fehler" subtitle={e?.response?.data?.message} />,
        );
      }
    } else {
      setQrCodeDialogOpen(true);
    }
  };

  const delete2FA = async () => {
    try {
      const response = await auth.post('/otp/disable');
      const { disabled } = response.data;

      if (disabled) {
        setAuthenticators([]);
        setSecret('');
        setQrCode('');
        toast.success(
          <ToastMessage
            title="Erfolg"
            subtitle="Zwei-Faktor-Authentifizierung erfolgreich deaktiviert"
          />,
        );
      } else {
        toast.error(<ToastMessage title="Fehler" />);
      }
    } catch (e) {
      toast.error(
        <ToastMessage
          title="Fehler beim Deaktivieren der Zwei-Faktor-Authentifizierung"
          subtitle={e?.response?.data?.message}
        />,
      );
    }
  };

  const verifyToken = async (token) => {
    try {
      const response = await auth.post(
        '/otp/verify',
        JSON.stringify({ token, secret }),
      );
      const { verified, authenticators } = response.data;

      if (verified) {
        setSecret('');
        setQrCode('');
        setAuthenticators(authenticators);
        setQrCodeDialogOpen(false);
        toast.success(
          <ToastMessage
            title="Erfolg"
            subtitle="Zwei-Faktor-Authentifizierung erfolgreich aktiviert"
          />,
        );
      } else {
        toast.error(<ToastMessage title="Fehler" />);
      }
    } catch (e) {
      toast.error(
        <ToastMessage
          title="Fehler beim Deaktivieren der Zwei-Faktor-Authentifizierung"
          subtitle={e?.response?.data?.message}
        />,
      );
    }
  };

  return (
    <>
      {authenticators.length > 0 ? (
        <>
          <Typography sx={{ margin: 2 }}>
            Zweifaktor-Authentifizierung ist aktiviert.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={delete2FA}
          >
            2 FA löschen
          </Button>
        </>
      ) : (
        <>
          <Typography sx={{ margin: 2 }}>
            Zweifaktor-Authentifizierung ist nicht aktiviert.
          </Typography>

          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={setup2FA}
          >
            2 FA hinzufügen
          </Button>
        </>
      )}

      <Dialog
        open={qrCodeDialogOpen}
        onClose={() => {
          setQrCodeDialogOpen(false);
        }}
      >
        <DialogTitle>QR Code für Zwei-Faktor-Authentifizierung</DialogTitle>
        <DialogContent>
          {qrCode && (
            <Box display="flex" justifyContent="center" mb={2}>
              <img
                src={qrCode}
                alt="QR Code"
                style={{ width: '200px', height: '200px' }}
              />
            </Box>
          )}
          <Typography gutterBottom>
            Scannen Sie diesen QR-Code mit Ihrer Authenticator-App, um die
            Zwei-Faktor-Authentifizierung zu aktivieren.
          </Typography>
          <Typography gutterBottom>
            Wenn Sie keinen QR-Code-Scanner haben, können Sie den folgenden
            Schlüssel manuell eingeben:
          </Typography>

          <Box
            display="flex"
            justifyContent="center"
            mb={2}
            p={2}
            sx={{
              backgroundColor: '#f0f0f0',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            <TextField
              disabled
              value={secret}
              variant="outlined"
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => {
                        navigator.clipboard.writeText(secret);
                      }}
                    >
                      <ContentCopy />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ wordBreak: 'break-word' }}
            />
          </Box>
          <Typography gutterBottom>
            Geben Sie nun einen Token ein, der von Ihrer Authenticator-App
            generiert wird, damit wir die Einrichtung abschließen können.
          </Typography>
          <TextField
            fullWidth
            label="Token eingeben"
            variant="outlined"
            inputMode="numeric"
            pattern="[0-9]*"
            id="token-input"
            autoFocus
            autoComplete="one-time-code"
            sx={{ mt: 2 }}
            onInput={(e) => {
              if (e.target.value.length === 6) {
                verifyToken(e.target.value);
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQrCodeDialogOpen(false)} color="primary">
            Schließen
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
